// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable i18next/no-literal-string */
import { datadogRum } from '@datadog/browser-rum'
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'

import { DD_RUM_ENABLED_FLAG } from '@src/config/env'
import { STATSIG_EXPERIMENTS, STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { QuinceRouter } from '@src/modules/navigation'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { getAdditionalProductTagsForCard } from '@src/quince-site-common/src/utils/festivalOffersUtility'
import { GenderName, getCurrentPageGenderContext } from '@src/quince-site-common/src/utils/getCurrentPageGenderContext'
import { getPathName } from '@src/quince-site-common/src/utils/getPathName'
import { getPathParams } from '@src/quince-site-common/src/utils/getPathParam'
import { getURLStringFromInputString } from '@src/quince-site-common/src/utils/getURLStringFromInputString'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { isEmpty } from '@src/utils/functions/utils'

import { CardVariant, IProductCard, ProductCardTag } from './product-card.types'
// @i18n-ignore
export const QUERY_PARAM_TRACKER = 'tracker'
export const QUERY_PARAM_SEARCH_QUERY = 'searchQuery'
export const QUERY_PARAM_PRODUCT_POSITION = 'productPosition'
export const QUERY_PARAM_VALUE_SEARCH_PAGE_SEARCH_SECTION_SEARCH_RESULTS = 'searchPage__search_section__search_results'

export function getInitialSelectedVariant({
  selectedCardVariantId = undefined,
  cardVariants = [],
}: Pick<IProductCard['product'], 'cardVariants'> & { selectedCardVariantId?: number }) {
  if (!selectedCardVariantId) return cardVariants[0]
  const cardVariant = cardVariants.find(cardVariant => cardVariant.variantId === selectedCardVariantId)
  return cardVariant ?? cardVariants[0]
}

export function moveSelectedVariantToFront(cardVariants: CardVariant[] = [], selectedColor?: string) {
  const updatedCardVariants = [...cardVariants]
  const selectedVariantIndex = updatedCardVariants.findIndex(
    cardVariant => cardVariant.displayConfig.value === selectedColor,
  )
  const deletedElement = updatedCardVariants.splice(selectedVariantIndex, 1)[0]
  if (deletedElement) {
    updatedCardVariants.unshift(deletedElement)
    return updatedCardVariants
  }
  return cardVariants
}

export function getUpdatedProductCardForSelectedColor(productCard: IProductCard, selectedColor?: string): IProductCard {
  const updatedCardVariants = moveSelectedVariantToFront(productCard.product.cardVariants, selectedColor)
  return { ...productCard, product: { ...productCard.product, cardVariants: updatedCardVariants } }
}

export function getSelectedVariantForSelectedColor({
  cardVariants = [],
  selectedColor,
}: {
  cardVariants: CardVariant[]
  selectedColor?: string
}) {
  if (!selectedColor) {
    return undefined
  }

  return cardVariants.find(cardVariant => cardVariant.displayConfig.value === selectedColor)
}

export const navigateToPDP = ({
  slug,
  tracker,
  selectedOption,
  additionalAttributes,
  router,
}: {
  slug: string
  tracker: string | undefined
  selectedOption?: string
  additionalAttributes?: Record<string, string>
  router: AppRouterInstance | QuinceRouter
}) => {
  const productLink = getProductLink(slug, selectedOption, tracker, additionalAttributes)
  router.push(productLink)
}

export const getProductLink = (
  slug: string,
  selectedOption?: string,
  tracker?: string,
  additionalAttributes?: Record<string, string>,
  searchQuery?: string,
  position?: number,
) => {
  const params: Record<string, string> = {}
  const color = selectedOption
  if (color) {
    params.color = getURLStringFromInputString(color)
  }
  if (tracker) {
    params[QUERY_PARAM_TRACKER] = tracker
  }
  if (searchQuery) {
    params[QUERY_PARAM_SEARCH_QUERY] = searchQuery
  }
  if (position) {
    params[QUERY_PARAM_PRODUCT_POSITION] = position.toString()
  }

  if (additionalAttributes && !isEmpty(additionalAttributes)) {
    Object.entries(additionalAttributes).forEach(([value, key]) => (params[key] = value.toLowerCase()))
  }

  let currentPageSlug = ''
  if (isBrowser()) {
    currentPageSlug = window.location.pathname
  }
  return getPathName(slug) + getPathParams(params, currentPageSlug)
}

export const getProductLinkWithSelectedColor = (slug: string, selectedCardVariant?: CardVariant) => {
  const params: Record<string, string> = {}

  const selectedColor = selectedCardVariant?.displayConfig.value
  if (selectedColor) {
    params.color = getURLStringFromInputString(selectedColor)
  }

  return getPathName(slug) + getPathParams(params, '')
}

export function reportEmptyProductImages(
  selectedVariant?: CardVariant,
  gender?: GenderName | undefined,
  deviceType?: DEVICE_TYPES,
) {
  if (!isBrowser()) {
    return
  }

  try {
    const productId = selectedVariant?.variantId
    const colorName = selectedVariant?.displayConfig.value
    // @i18n-ignore
    const errorMessage = `Product Card Images array is empty for variant ${productId} color ${colorName ?? 'unknown'}`

    const debugData = {
      variant: selectedVariant
        ? {
            variantId: selectedVariant.variantId,
            title: selectedVariant.title,
            vendorType: selectedVariant.vendorType,
            url: selectedVariant.url,
            svids: selectedVariant.svids,
            displayConfig: selectedVariant.displayConfig,
            price: selectedVariant.price,
            tags: selectedVariant.tags,
          }
        : null,
      timestamp: new Date().toISOString(),
      pageUrl: window.location.href,
      pathname: window.location.pathname,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      deviceType: deviceType,
      gender: gender,
      sessionId: sessionStorage.getItem('sessionId'),
      isServerSide: false,
    }

    if (DD_RUM_ENABLED_FLAG) {
      try {
        datadogRum.addError(new Error(errorMessage), {
          tags: {
            type: 'EMPTY_PRODUCT_IMAGES',
            module: MODULES.PRODUCT_CARD,
            feature: FEATURES.IMAGE,
            productId: productId ?? 'unknown',
            colorName: colorName ?? 'unknown',
            variantTitle: selectedVariant?.title ?? 'unknown',
            vendorType: selectedVariant?.vendorType ?? 'unknown',
          },
          context: {
            debugInfo: debugData,
          },
        })
      } catch (rumError) {
        console.error('Failed to report to DataDog RUM:', rumError)
      }
    }
  } catch (error) {
    console.error('Failed to report empty product images:', error)
  }
}

export function getImagesForOptions(selectedVariant?: CardVariant, gender?: GenderName | undefined) {
  const productMedia = selectedVariant?.images.filter(image => {
    if (
      (image.description as GenderName) === GenderName.MALE ||
      (image.description as GenderName) === GenderName.FEMALE
    ) {
      let currentPageGenderContext: GenderName | undefined
      if (gender) {
        currentPageGenderContext = gender
      } else if (isBrowser()) {
        const pathname = window.location.pathname
        const search = window.location.search
        currentPageGenderContext = getCurrentPageGenderContext(pathname, search)
      }

      if (currentPageGenderContext && currentPageGenderContext !== (image.description as GenderName)) {
        return false
      }
    }
    return true
  })

  return productMedia ?? []
}

export function getSelectedOptionsForProductCard(selectedCardVariant?: CardVariant): ProductVariantOption[] {
  const selectedOptions: ProductVariantOption[] = []

  if (selectedCardVariant) {
    selectedOptions.push({
      name: selectedCardVariant.displayConfig.name,
      value: selectedCardVariant.displayConfig.value,
    })
  }
  return selectedOptions
}

function removeDuplicateTags(tags: ProductCardTag[]) {
  const tagsSet = new Set()
  return tags.filter(tag => {
    if (tagsSet.has(tag.value)) {
      return false
    }
    tagsSet.add(tag.value)
    return true
  })
}

export function getVariantTags(
  selectedCardVariant?: CardVariant,
  isCreditCardControlBucket?: boolean,
  refreshedTagsForSelectedVariant?: ProductCardTag[],
  urgencyTagsBucket?: string | null,
) {
  let allTags = refreshedTagsForSelectedVariant ?? selectedCardVariant?.tags ?? []

  const isSoldOut = allTags.find(applicableVariantTag => applicableVariantTag.value === 'Sold Out')
  const additionalProductTags = getAdditionalProductTagsForCard(selectedCardVariant)

  if (!isSoldOut && additionalProductTags.length > 0) {
    allTags = additionalProductTags
  }

  const showDefaultTags =
    urgencyTagsBucket !== STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.URGENCY_TAGS].buckets?.groupB

  const applicableTags = removeDuplicateTags(allTags)
    .filter(item => {
      switch (item.value) {
        case 'GET IT BY 02/14':
          return false
        case 'Today Only':
          return false
        case 'Best seller':
          return showDefaultTags
        case 'Low Stock':
          return showDefaultTags
        default:
          return true
      }
    })
    .filter(
      item => !(isCreditCardControlBucket && item.value.includes('Earn $') && selectedCardVariant?.vendorType == '3P'),
    )

  return {
    applicableTags,
  }
}

export const getQuincePriceForProduct = (cardVariants: CardVariant[]) =>
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  Math.min(...cardVariants.map(variant => variant?.price?.salePrice).filter(val => !!val))

export const hasDifferentPrices = (cardVariants: CardVariant[]) => {
  const somePrice = cardVariants[0]?.price.salePrice
  for (let index = 1; index < cardVariants.length; index++) {
    const variant = cardVariants[index]
    if (somePrice !== variant?.price.salePrice) {
      return true
    }
  }
  return false
}

export const getAllVariantIds = (cardVariants: CardVariant[] = []): string[] => {
  const variantIdsSet = new Set<string>()

  cardVariants.forEach(variant => {
    if (Array.isArray(variant.variantIds)) {
      variant.variantIds.forEach(id => {
        if (id) {
          variantIdsSet.add(id.toString())
        }
      })
    }
  })

  return Array.from(variantIdsSet)
}
